<template>
  <div ref="root" class="bzp-root" :style="rootStyle" data-test="chat-page">
    <!-- Same gate as the panel (flag, BVR, denied): until it passes the page shows nothing but this note. -->
    <div v-if="gate === 'hidden'" class="bzp-card bzp-unavailable" role="status" data-test="chat-page-unavailable">
      Ask Bizminer isn't available for this account.
    </div>

    <div v-else class="bzp-grid">
      <section v-show="showList" class="bzp-card bzp-list" aria-label="Conversations" data-test="chat-page-list">
        <div class="bzp-list-head">
          <div class="bzp-list-title-row">
            <h1 class="bzp-h1">Ask Bizminer</h1>
            <button type="button" class="bzp-pill" :disabled="panelBusy" data-test="chat-page-new" @click="newConversation">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path></svg>
              New conversation
            </button>
          </div>
          <p v-if="panelBusy" class="bzp-busy" role="status" data-test="chat-page-busy">Finishing the current answer&hellip;</p>
          <label class="bzp-search">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#6b7686"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>
            <span class="bzp-sr">Search conversations</span>
            <input
              v-model="query"
              type="search"
              maxlength="100"
              autocomplete="off"
              placeholder="Search conversations"
              data-test="chat-page-search"
              @keydown.enter.prevent="searchNow"
            />
          </label>
        </div>

        <div ref="listEl" class="bzp-rows" data-test="chat-page-rows" @scroll.passive="onListScroll">
          <div v-if="view === 'loading'" class="bzp-status" role="status">
            <svg class="bzp-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
            Loading conversations&hellip;
          </div>

          <div v-else-if="view === 'error'" class="bzp-error" role="alert" data-test="chat-page-list-error">
            <span>Couldn't load your conversations.</span>
            <button type="button" class="bzp-link" @click="loadFirst">Retry</button>
          </div>

          <div v-else-if="view === 'empty'" class="bzp-empty" data-test="chat-page-empty-state">
            <div class="bzp-empty-title">{{ emptyTitle }}</div>
            <p>{{ emptyText }}</p>
          </div>

          <p v-else-if="view === 'nomatch'" class="bzp-nomatch" role="status" data-test="chat-page-no-match">{{ noMatch }}</p>

          <ul v-else class="bzp-ul">
            <li v-for="c in items" :key="c.id" class="bzp-li" :class="{ 'is-current': c.id === selectedId }">
              <button
                type="button"
                class="bzp-row"
                :aria-current="c.id === selectedId ? 'true' : null"
                :disabled="panelBusy"
                data-test="chat-page-row"
                @click="select(c)"
              >
                <span class="bzp-row-title">{{ c.title }}</span>
                <span class="bzp-row-meta">
                  <span>{{ when(c) }}</span>
                  <span v-if="c.messageCount">&middot; {{ c.messageCount }} {{ c.messageCount === 1 ? 'message' : 'messages' }}</span>
                  <!-- CHAT-08: shared with Bizminer support. -->
                  <span v-if="c.sharedAt" class="bzp-badge" title="Bizminer support can read this conversation" data-test="chat-page-row-shared">Shared</span>
                </span>
              </button>
            </li>
          </ul>

          <div v-if="moreLoading" class="bzp-status" role="status" data-test="chat-page-more-loading">
            <svg class="bzp-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
            Loading more&hellip;
          </div>
          <div v-else-if="moreError" class="bzp-error bzp-error-more" role="alert">
            <span>Couldn't load more.</span>
            <button type="button" class="bzp-link" @click="loadMore(true)">Retry</button>
          </div>
        </div>

        <p class="bzp-foot">Conversations are kept for 12 months.</p>
      </section>

      <section v-show="layout.convo" class="bzp-card bzp-convo" :class="{ 'is-teaser': gate === 'teaser' }" aria-label="Conversation" data-test="chat-page-conversation">
        <div v-if="gate === 'full'" class="bzp-convo-head">
          <button v-if="layout.back" type="button" class="bzp-back" aria-label="Back to conversations" data-test="chat-page-back" @click="back">
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path></svg>
          </button>
          <h2 class="bzp-h2">{{ convoTitle }}</h2>
          <span v-if="selectedShared" class="bzp-badge" title="Bizminer support can read this conversation" data-test="chat-page-shared">Shared</span>
          <div class="bzp-actions">
            <!-- CHAT-08: Share with support / Stop sharing, with a confirm step. -->
            <chat-share-menu
              v-if="selectedId"
              variant="button"
              :conversation-id="selectedId"
              :shared="selectedShared"
              :share-note="shareNotes[selectedId] || ''"
              :disabled="panelBusy"
              @shared="onShared"
              @unshared="onUnshared"
              @missing="onMissing"
              @access="handleAccess"
            />
            <slot name="conversation-actions" :conversation-id="selectedId"></slot>
            <button v-if="selectedId" type="button" class="bzp-ghost bzp-danger-text" aria-label="Delete conversation" :disabled="panelBusy" data-test="chat-page-delete" @click="askDelete">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
              <span class="bzp-btn-label">Delete</span>
            </button>
          </div>
        </div>

        <div
          v-if="confirmId"
          ref="confirmEl"
          class="bzp-confirm"
          role="alertdialog"
          aria-labelledby="bzp-confirm-text"
          data-test="chat-page-delete-dialog"
          @keydown.esc.prevent="cancelDelete"
        >
          <span id="bzp-confirm-text" class="bzp-confirm-text">{{ deleteConfirmText }}</span>
          <button type="button" class="bzp-danger" :disabled="deleting || panelBusy" data-test="chat-page-delete-confirm" @click="doDelete">{{ deleting ? 'Deleting…' : 'Delete' }}</button>
          <button ref="cancelBtn" type="button" class="bzp-ghost" :disabled="deleting" data-test="chat-page-delete-cancel" @click="cancelDelete">Cancel</button>
        </div>

        <div v-if="notice" class="bzp-notice" role="alert" data-test="chat-page-notice">
          <span>{{ notice }}</span>
          <button type="button" class="bzp-link" @click="notice = ''">Dismiss</button>
        </div>

        <div class="bzp-panel-wrap">
          <ask-bizminer-panel
            embedded
            :open-id="selectedId"
            @ready="onReady"
            @conversation="onCreated"
            @changed="refreshHead"
            @working="panelBusy = $event"
            @dirty="panelDirty = $event"
            @missing="onMissing"
            @share-state="onShareState"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
// The /chat page (CHAT-13): conversation history on the left, the open conversation on the right.
// The right side IS the panel (AskBizminerPanel in embedded mode), so messages, charts, cards,
// citations, the composer, purchases and the teaser are one implementation. This component owns
// only the history: the list, search, infinite scroll, delete, and the phone layout.
import AskBizminerPanel from './AskBizminerPanel.vue';
import ChatShareMenu from './ChatShareMenu.vue';
import {
  PHONE_MAX_WIDTH,
  SEARCH_DEBOUNCE_MS,
  EMPTY_LIST_TITLE,
  EMPTY_LIST_TEXT,
  DELETE_CONFIRM_TEXT,
  normalizeSearch,
  mergeConversationPage,
  mergeConversationHead,
  conversationListView,
  reloadClearsRows,
  noMatchText,
  deleteFailure,
  afterDelete,
  canLoadMore,
  shouldAutoSelect,
  createDebouncer,
  nearBottom,
  whenLabel,
  chatPageLayout,
  chatPageGate,
  isSubscriptionRequired,
  entitlementFromError,
  setSharedAt,
  seedSharedMap,
} from './chatCore';
import { isDenied, listConversations, deleteConversation, withReauth } from './chatApi';

const MARGIN_BELOW = 24; // px of page background kept under the cards
const MIN_HEIGHT = 420;

export default {
  name: 'chat-page',
  components: { AskBizminerPanel, ChatShareMenu },
  data() {
    return {
      items: [],
      nextCursor: null,
      emptyState: false,
      query: '', // what is typed
      applied: '', // what the list was last asked for (after the debounce)
      listLoading: false,
      listError: false,
      moreLoading: false,
      moreError: false,
      selectedId: null, // null = a new conversation
      shareNotes: {}, // id -> the service's note on what sharing means, once it has said it
      sharedMap: {}, // id -> sharedAt of a conversation shared or unshared here whose row a search may hide
      titles: {}, // id -> title of every row seen, so the open conversation keeps its title when a search hides its row
      pane: 'list', // phones: which pane shows
      isPhone: false,
      panelBusy: false, // an answer is streaming or settling, or a purchase click runs in the panel: the list holds still
      panelDirty: false, // the panel has a draft or messages on screen
      startedNew: false, // the user already chose (a row or New conversation): no auto-open after that
      pageQuery: '', // the query the loaded rows belong to
      connected: false, // the panel finished its handshake, so the history can be read
      confirmId: null,
      deleting: false,
      notice: '',
      emptyTitle: EMPTY_LIST_TITLE,
      emptyText: EMPTY_LIST_TEXT,
      deleteConfirmText: DELETE_CONFIRM_TEXT,
      fitHeight: 0, // px from the page's top edge to the bottom of the window (0 = not measured: CSS fallback)
    };
  },
  computed: {
    visible() {
      return this.$store.getters['chat/visible'];
    },
    mode() {
      return this.$store.getters['chat/mode'];
    },
    gate() {
      return chatPageGate({ visible: this.visible, mode: this.mode });
    },
    rootStyle() {
      return this.fitHeight ? { height: this.fitHeight + 'px' } : null;
    },
    layout() {
      return chatPageLayout({ isPhone: this.isPhone, pane: this.pane });
    },
    showList() {
      return this.gate === 'full' && this.layout.list;
    },
    view() {
      return conversationListView({ items: this.items, query: this.applied, loading: this.listLoading, error: this.listError });
    },
    noMatch() {
      return noMatchText(this.applied);
    },
    selectedItem() {
      return this.items.find((c) => c.id === this.selectedId) || null;
    },
    // CHAT-08: the open conversation is shared with support (its row, or what was just done here when no row is listed).
    selectedShared() {
      if (!this.selectedId) return false;
      return this.selectedItem ? !!this.selectedItem.sharedAt : !!this.sharedMap[this.selectedId];
    },
    convoTitle() {
      if (this.selectedItem) return this.selectedItem.title;
      if (!this.selectedId) return 'New conversation';
      return this.titles[this.selectedId] || 'Conversation';
    },
  },
  watch: {
    // Typing: the list is asked for only after 300 ms without a keystroke.
    query(v) {
      this.debounced.call(normalizeSearch(v));
    },
    // Logout, a different user, or lost access: forget the history; it is read again when the panel is ready.
    gate(g, was) {
      if (g === 'hidden') this.forget();
      if (g === 'full' && was !== 'full' && this.connected) this.loadFirst();
    },
  },
  created() {
    // Two chat surfaces at once would be two conversations: the side sheet closes while this page is open.
    this.$store.dispatch('chat/closePanel');
    this.seq = 0; // guards stale list answers (not reactive on purpose)
    this.autoSelected = false;
    this.debounced = createDebouncer((term) => this.applySearch(term), SEARCH_DEBOUNCE_MS);
  },
  mounted() {
    this.watchViewport();
    this.measure();
    window.addEventListener('resize', this.measure);
    // The layout above the page (top bar, banners) can settle after the first paint.
    this.measureTimer = setTimeout(this.measure, 400);
  },
  beforeDestroy() {
    this.debounced.cancel();
    this.seq++;
    this.unwatchViewport();
    window.removeEventListener('resize', this.measure);
    clearTimeout(this.measureTimer);
  },
  methods: {
    // --- viewport -------------------------------------------------------------
    // The page fills what is left of the window below whatever sits above it, so the
    // composer is always on screen and only the lists and messages scroll.
    measure() {
      const el = this.$refs.root;
      if (!el) return;
      const top = el.getBoundingClientRect().top + (window.pageYOffset || 0);
      const h = Math.floor(window.innerHeight - top - MARGIN_BELOW);
      this.fitHeight = Math.max(MIN_HEIGHT, h);
    },
    watchViewport() {
      this.mq = window.matchMedia ? window.matchMedia('(max-width: ' + PHONE_MAX_WIDTH + 'px)') : null;
      this.onViewport();
      if (!this.mq) return;
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onViewport);
      else if (this.mq.addListener) this.mq.addListener(this.onViewport);
    },
    unwatchViewport() {
      if (!this.mq) return;
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onViewport);
      else if (this.mq.removeListener) this.mq.removeListener(this.onViewport);
    },
    onViewport() {
      this.isPhone = !!(this.mq && this.mq.matches);
    },

    // --- the list ---------------------------------------------------------------
    // Errors every call shares: the service says no more chat (hide), or the subscription is gone (teaser).
    // Resolves true when it was one of those, so the caller shows nothing of its own.
    handleAccess(e) {
      if (isDenied(e)) {
        this.$store.dispatch('chat/deny');
        return true;
      }
      if (isSubscriptionRequired(e)) {
        this.$store.dispatch('chat/setEntitlement', entitlementFromError(e));
        return true;
      }
      return false;
    },
    onReady() {
      this.connected = true;
      this.loadFirst();
    },
    forget() {
      this.seq++;
      this.connected = false;
      this.items = [];
      this.nextCursor = null;
      this.emptyState = false;
      this.listLoading = false;
      this.listError = false;
      this.moreLoading = false;
      this.moreError = false;
      this.selectedId = null;
      this.pane = 'list';
      this.panelBusy = false; // the panel is gone (or about to be), so it can no longer say it finished
      this.panelDirty = false;
      this.confirmId = null;
      this.notice = '';
      this.query = '';
      this.applied = '';
      this.titles = {};
      this.shareNotes = {};
      this.sharedMap = {};
      this.debounced.cancel();
      this.autoSelected = false;
      this.startedNew = false;
      this.pageQuery = '';
    },
    // Page 1 for the current search.
    async loadFirst() {
      const token = ++this.seq;
      this.listLoading = true;
      this.listError = false;
      this.moreLoading = false;
      this.moreError = false;
      const q = this.applied;
      // Rows of another query must not stay on screen (or be paged) while this one loads. The same query
      // (reconnect, gate return, Retry) keeps its rows, and keeps them if the refresh fails.
      if (reloadClearsRows(q, this.pageQuery)) {
        this.items = [];
        this.nextCursor = null;
      }
      this.pageQuery = q;
      try {
        const page = await withReauth(() => listConversations({ q }));
        if (token !== this.seq) return;
        this.items = page.items;
        this.remember(page.items);
        this.nextCursor = page.nextCursor;
        this.emptyState = page.emptyState;
        this.autoSelectFirst();
      } catch (e) {
        if (token !== this.seq) return;
        // With no rows the error banner shows; rows already on screen stay.
        if (!this.handleAccess(e)) this.listError = true;
      } finally {
        if (token === this.seq) {
          this.listLoading = false;
          this.$nextTick(this.fill);
        }
      }
    },
    // Wide screens open the newest conversation on arrival, like the design; phones start at the list.
    autoSelectFirst() {
      if (this.autoSelected) return;
      this.autoSelected = true;
      const open = shouldAutoSelect({
        isPhone: this.isPhone,
        selectedId: this.selectedId,
        applied: this.applied,
        count: this.items.length,
        typed: this.panelDirty,
        started: this.startedNew,
        hasMessages: this.panelDirty,
      });
      if (open) this.selectedId = this.items[0].id;
    },
    // The next page (infinite scroll). `retry` = the Retry button after a failed one.
    async loadMore(retry) {
      const q = this.applied;
      const can = canLoadMore({
        nextCursor: this.nextCursor,
        listLoading: this.listLoading,
        moreLoading: this.moreLoading,
        moreError: this.moreError,
        retry,
        pageQuery: this.pageQuery,
        applied: q,
      });
      if (!can) return;
      const token = this.seq;
      this.moreLoading = true;
      this.moreError = false;
      try {
        const page = await withReauth(() => listConversations({ cursor: this.nextCursor, q }));
        if (token !== this.seq) return;
        this.items = mergeConversationPage(this.items, page.items);
        this.remember(page.items);
        this.nextCursor = page.nextCursor;
      } catch (e) {
        if (token !== this.seq) return;
        if (!this.handleAccess(e)) this.moreError = true;
      } finally {
        if (token === this.seq) {
          this.moreLoading = false;
          this.$nextTick(this.fill);
        }
      }
    },
    remember(rows) {
      const next = { ...this.titles };
      rows.forEach((r) => {
        next[r.id] = r.title;
      });
      this.titles = next;
    },
    onListScroll() {
      if (nearBottom(this.$refs.listEl)) this.loadMore();
    },
    // Rows that do not fill the box leave nothing to scroll: keep asking until they do (or there is no more).
    fill() {
      if (this.nextCursor && !this.moreError && nearBottom(this.$refs.listEl)) this.loadMore();
    },
    // After a turn or a purchase: new and moved conversations show first, the loaded pages stay.
    async refreshHead() {
      if (this.gate !== 'full' || this.listLoading) return;
      const token = this.seq;
      const q = this.applied;
      try {
        const page = await withReauth(() => listConversations({ q }));
        if (token !== this.seq || q !== this.applied || q !== this.pageQuery) return;
        const merged = mergeConversationHead(this.items, this.nextCursor, page);
        this.items = merged.items;
        this.remember(page.items);
        this.nextCursor = merged.nextCursor;
        this.emptyState = page.emptyState;
        this.listError = false;
      } catch (e) {
        if (token === this.seq) this.handleAccess(e); // otherwise the list is only a little behind
      }
    },

    // --- search -----------------------------------------------------------------
    applySearch(term) {
      if (term === this.applied) return;
      this.applied = term;
      this.loadFirst();
    },
    // Enter: no waiting for the 300 ms.
    searchNow() {
      this.debounced.flush();
    },

    // --- choosing a conversation ----------------------------------------------
    select(c) {
      if (this.panelBusy) return;
      this.startedNew = true;
      this.cancelDelete();
      this.notice = '';
      this.selectedId = c.id;
      this.pane = 'convo';
    },
    newConversation() {
      if (this.panelBusy) return;
      this.startedNew = true;
      this.cancelDelete();
      this.notice = '';
      this.selectedId = null;
      this.pane = 'convo';
    },
    back() {
      this.pane = 'list';
    },
    // The panel made a conversation (the first message of a new one): it is the open one now.
    onCreated(id) {
      this.selectedId = id;
    },
    // The panel found the conversation gone (deleted elsewhere): drop the row.
    onMissing(id) {
      this.removeLocal(id);
      this.notice = 'That conversation is no longer available.';
    },

    // --- share with support (CHAT-08) -------------------------------------------------
    onShared({ id, sharedAt, shareNote }) {
      this.items = setSharedAt(this.items, id, sharedAt);
      this.sharedMap = { ...this.sharedMap, [id]: sharedAt };
      if (shareNote) this.shareNotes = { ...this.shareNotes, [id]: shareNote };
    },
    // The panel read the open conversation's share state: the page follows it (row and map).
    onShareState({ id, sharedAt, shareNote }) {
      if (id == null) return;
      this.items = setSharedAt(this.items, id, sharedAt);
      this.sharedMap = seedSharedMap(this.sharedMap, id, sharedAt);
      if (shareNote && this.shareNotes[id] !== shareNote) this.shareNotes = { ...this.shareNotes, [id]: shareNote };
    },
    onUnshared({ id }) {
      this.items = setSharedAt(this.items, id, null);
      this.sharedMap = { ...this.sharedMap, [id]: null };
    },

    // --- delete -----------------------------------------------------------------
    askDelete() {
      if (!this.selectedId || this.panelBusy) return;
      this.notice = '';
      this.confirmId = this.selectedId;
      this.$nextTick(() => this.$refs.cancelBtn && this.$refs.cancelBtn.focus());
    },
    cancelDelete() {
      if (this.deleting) return;
      this.confirmId = null;
    },
    async doDelete() {
      const id = this.confirmId;
      if (!id || this.deleting) return;
      this.deleting = true;
      try {
        await withReauth(() => deleteConversation(id));
        this.removeLocal(id);
      } catch (e) {
        const f = deleteFailure(e);
        if (f.remove) this.removeLocal(id);
        else if (f.kind === 'denied' || f.kind === 'subscription') this.handleAccess(e);
        else this.notice = f.text; // busy: "Can't delete while an answer or purchase is in progress"
      } finally {
        this.deleting = false;
        this.confirmId = null;
      }
    },
    removeLocal(id) {
      const r = afterDelete(this.items, id, this.selectedId);
      this.items = r.items;
      if (this.selectedId === id) {
        // Wide: the neighbour opens, or a new conversation when the row is not listed (a search hides it).
        // Phone: back to the list. Either way the panel leaves the deleted conversation.
        this.selectedId = this.isPhone ? null : r.selectedId;
        if (this.isPhone) this.pane = 'list';
      }
      this.$nextTick(this.fill);
    },

    when(c) {
      return whenLabel(c.updatedAt);
    },
  },
};
</script>

<style lang="scss">
$bzp-navy: #003666;
$bzp-cyan: #0cbcf2;

.bzp-root {
  // Fallback until the page measures itself (see measure()).
  height: calc(100vh - 150px);
  min-height: 420px;
  font-family: Roboto, Arial, sans-serif;
  color: #2b3445;
  text-align: left;

}
// Resets for the page's own chrome only: the embedded panel (.bzp-panel-wrap) has its own styles.
.bzp-list, .bzp-convo-head, .bzp-confirm, .bzp-notice {
  button { font: inherit; cursor: pointer; }
  button:disabled { cursor: default; }
}
.bzp-list p { margin: 0; }
.bzp-sr { position: absolute; left: -9999px; }
.bzp-spin { animation: bzp-spin 0.9s linear infinite; transform-origin: 50% 50%; }
@keyframes bzp-spin { to { transform: rotate(360deg); } }

.bzp-grid { display: flex; gap: 24px; height: 100%; min-height: 0; }
.bzp-card { background: #fff; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12); display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.bzp-unavailable { padding: 28px 24px; font-size: 14px; color: #3c4656; }

.bzp-list { flex: 0 1 340px; max-width: 340px; }
.bzp-convo { flex: 999 1 520px; overflow: hidden; }

.bzp-list-head { padding: 16px 16px 12px; display: flex; flex-direction: column; gap: 12px; border-bottom: 1px solid #e6eaef; }
.bzp-list-title-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 10px; }
.bzp-h1 { margin: 0; white-space: nowrap; font-family: Inter, Roboto, sans-serif; font-size: 19px; font-weight: 600; color: $bzp-navy; }
.bzp-pill {
  display: inline-flex; align-items: center; gap: 5px; min-height: 36px; padding: 0 12px 0 10px; border: 0; border-radius: 18px;
  background: $bzp-navy; color: #fff; font-size: 13px; font-weight: 500; white-space: nowrap;
  &:hover:not(:disabled) { background: #002a50; }
  &:disabled { opacity: 0.55; }
  &:focus-visible { outline: 3px solid $bzp-cyan; outline-offset: 2px; }
}
.bzp-search {
  display: flex; align-items: center; gap: 8px; margin: 0; border: 1px solid #c9d1db; border-radius: 8px; padding: 0 10px; height: 38px;
  &:focus-within { border-color: $bzp-cyan; box-shadow: 0 0 0 2px rgba(12, 188, 242, 0.2); }
  input { border: 0; outline: none; flex: 1 1 auto; min-width: 0; font-size: 13.5px; background: transparent; color: #13213c; height: 100%; }
}

.bzp-rows { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.bzp-ul { list-style: none; margin: 0; padding: 0; }
.bzp-li { border-bottom: 1px solid #eef1f4; background: #fff; &.is-current { background: #e7f3fa; box-shadow: inset 3px 0 0 $bzp-cyan; } }
.bzp-row {
  display: flex; flex-direction: column; gap: 3px; width: 100%; text-align: left; border: 0; background: transparent; padding: 12px 16px; min-height: 44px;
  &:hover:not(:disabled) { background: #f2f8fc; }
  &:disabled { opacity: 0.6; }
  &:focus-visible { outline: 2px solid $bzp-cyan; outline-offset: -2px; }
}
.bzp-row-title { font-size: 13.5px; font-weight: 700; color: $bzp-navy; line-height: 1.35; overflow-wrap: anywhere; }
.bzp-row-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 0 6px; font-size: 11.5px; color: #6b7686; }
.bzp-badge {
  flex: 0 0 auto; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 7px; border-radius: 999px;
  background: #d8f1fb; color: #0a4a6e;
}

.bzp-status { display: flex; align-items: center; gap: 8px; padding: 14px 16px; font-size: 12.5px; color: #4f5a6b; }
.bzp-error {
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin: 12px 16px; font-size: 13px; line-height: 1.45; color: #a3261c;
  background: #fdf0ee; border: 1px solid #f1c9c4; border-radius: 8px; padding: 8px 10px;
}
.bzp-error-more { margin-top: 8px; }
.bzp-link { border: 0; background: transparent; padding: 0; font-weight: 700; color: #0a6fb3; text-decoration: underline; &:hover { color: $bzp-navy; } }
.bzp-empty { padding: 24px 18px; display: flex; flex-direction: column; gap: 8px; }
.bzp-empty-title { font-family: Inter, Roboto, sans-serif; font-size: 16px; font-weight: 600; color: $bzp-navy; }
.bzp-empty p { font-size: 13.5px; line-height: 1.5; color: #3c4656; }
.bzp-nomatch { padding: 20px 16px; font-size: 13px; color: #5b6575; overflow-wrap: anywhere; }
.bzp-busy { padding: 6px 10px; border-radius: 6px; background: #eaf6fc; font-size: 12px; color: #1f4f6e; }
.bzp-foot { padding: 12px 16px 14px; border-top: 1px solid #e6eaef; font-size: 11.5px; line-height: 1.45; color: #5b6575; }

.bzp-convo-head { flex: 0 0 auto; padding: 12px 20px; border-bottom: 1px solid #e6eaef; display: flex; align-items: center; gap: 12px; min-height: 62px; box-sizing: border-box; }
.bzp-h2 { margin: 0; flex: 1 1 auto; min-width: 0; font-family: Inter, Roboto, sans-serif; font-size: 18px; font-weight: 600; color: $bzp-navy; overflow-wrap: anywhere; }
.bzp-actions { display: flex; flex-wrap: wrap; gap: 8px; flex: 0 0 auto; }
.bzp-back {
  flex: 0 0 auto; width: 44px; height: 44px; margin-left: -10px; border: 0; border-radius: 22px; background: transparent; color: $bzp-navy;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: rgba(0, 54, 102, 0.08); }
  &:focus-visible { outline: 2px solid $bzp-cyan; outline-offset: -2px; }
}
.bzp-ghost {
  display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 12px; border-radius: 6px; border: 1px solid #c9d1db; background: #fff;
  color: #3c4656; font-size: 13px;
  &:hover:not(:disabled) { background: #f1f3f6; }
  &:disabled { opacity: 0.55; }
  &:focus-visible { outline: 2px solid $bzp-cyan; outline-offset: 2px; }
}
.bzp-danger-text { color: #a3261c; }
.bzp-danger {
  min-height: 36px; padding: 0 14px; border-radius: 6px; border: 0; background: #a3261c; color: #fff; font-size: 13px; font-weight: 500;
  &:hover:not(:disabled) { background: #8a1f17; }
  &:disabled { opacity: 0.55; }
  &:focus-visible { outline: 2px solid $bzp-cyan; outline-offset: 2px; }
}
.bzp-confirm {
  flex: 0 0 auto; margin: 14px 20px; padding: 12px 14px; border: 1px solid #f0c4bf; background: #fdf1f0; border-radius: 8px;
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px; font-size: 13px; color: #5c1a14;
}
.bzp-confirm-text { flex: 1 1 260px; line-height: 1.45; }
.bzp-notice {
  flex: 0 0 auto; margin: 14px 20px; padding: 10px 14px; background: #fff4d6; border-radius: 8px; font-size: 12.5px; line-height: 1.45; color: #6a4a00;
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px;
  span { flex: 1 1 220px; }
}
.bzp-panel-wrap { flex: 1 1 0; min-height: 0; display: flex; flex-direction: column; }
// The panel's own root element sits between the wrapper and the panel section.
.bzp-panel-wrap > .bzc-root { flex: 1 1 0; min-height: 0; display: flex; flex-direction: column; }

// Phones: one pane at a time (the list, then the conversation with a Back button).
@media (max-width: 600px) {
  .bzp-root { height: calc(100vh - 110px); height: calc(100dvh - 110px); }
  .bzp-grid { gap: 0; }
  .bzp-list { flex: 1 1 100%; max-width: none; }
  .bzp-convo { flex: 1 1 100%; }
  .bzp-convo-head { padding: 8px 12px 8px 14px; }
  .bzp-h2 { font-size: 16px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  // The Delete button is just its trash icon here (it keeps its aria-label).
  .bzp-actions .bzp-ghost { min-height: 44px; min-width: 44px; padding: 0; justify-content: center; }
  .bzp-actions .bzp-btn-label { position: absolute; left: -9999px; }
  .bzp-confirm .bzp-danger, .bzp-confirm .bzp-ghost { min-height: 44px; }
  .bzp-pill { min-height: 44px; }
  .bzp-search { height: 44px; input { font-size: 16px; } }
  .bzp-link { min-height: 44px; min-width: 44px; display: inline-flex; align-items: center; }
  .bzp-notice, .bzp-confirm { margin: 12px; }
}
</style>
